import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, Button, CircularProgress, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Alert, TextField, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { api, apiRequest } from '../../utils/api';

type Category = 'blog' | 'whitepaper' | 'press';
type PostType = 'hosted' | 'external';
type Status = 'draft' | 'published' | 'cancelled';

interface Resource {
  id: string;
  category: Category;
  postType: PostType;
  title: string;
  slug: string | null;
  description: string;
  body: string | null;
  coverImageUrl: string | null;
  externalUrl: string | null;
  displayDate: string;
  status: Status;
  sortOrder: number;
}

interface FormState {
  category: Category;
  post_type: PostType;
  title: string;
  slug: string;
  description: string;
  body: string;
  cover_image_url: string;
  external_url: string;
  display_date: string;
  sort_order: number;
  status: Status;
}

const EMPTY_FORM: FormState = {
  category: 'blog', post_type: 'external', title: '', slug: '', description: '',
  body: '', cover_image_url: '', external_url: '', display_date: '', sort_order: 0, status: 'draft',
};

const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const STATUS_CHIP: Record<Status, { label: string; bg: string; fg: string }> = {
  draft: { label: 'Draft', bg: '#FFF3E0', fg: '#B26A00' },
  published: { label: 'Published', bg: '#E8F5F3', fg: '#2D6E6A' },
  cancelled: { label: 'Cancelled', bg: '#FBE3E3', fg: '#C62828' },
};

const tealContained = { bgcolor: '#2D6E6A', textTransform: 'none' as const, borderRadius: 2, '&:hover': { bgcolor: '#1F4E4A' } };
const tealOutlined = { textTransform: 'none' as const, borderRadius: 2, color: '#2D6E6A', borderColor: '#2D6E6A' };
const redOutlined = { textTransform: 'none' as const, borderRadius: 2, color: '#d32f2f', borderColor: '#d32f2f' };

const previewSx = {
  border: '1px solid #E6E0D6', borderRadius: 2, p: 2, bgcolor: '#FAFAF8', minHeight: 120,
  color: '#4A5E5C', fontSize: '0.95rem',
  '& h1, & h2, & h3': { color: '#1A2826', fontWeight: 700, mt: 2, mb: 1 },
  '& h1': { fontSize: '1.5rem' }, '& h2': { fontSize: '1.3rem' }, '& h3': { fontSize: '1.1rem' },
  '& p': { lineHeight: 1.7, mb: 1.5 },
  '& a': { color: '#2D6E6A' },
  '& img': { maxWidth: '100%', borderRadius: 1, my: 1 },
  '& table': { borderCollapse: 'collapse', width: '100%', my: 1.5 },
  '& th, & td': { border: '1px solid #E6E0D6', px: 1, py: 0.5, textAlign: 'left' },
  '& th': { bgcolor: '#F0EDE6', fontWeight: 700 },
  '& ul, & ol': { pl: 3, mb: 1.5 },
  '& blockquote': { borderLeft: '3px solid #2D6E6A', pl: 1.5, ml: 0, fontStyle: 'italic' },
  '& code': { bgcolor: '#F0EDE6', px: 0.5, borderRadius: 0.5, fontFamily: 'monospace' },
  '& pre': { bgcolor: '#1A2826', color: '#fff', p: 1.5, borderRadius: 1, overflow: 'auto' },
};

const ResourcesTab: React.FC = () => {
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [processing, setProcessing] = useState<string | null>(null);

  const [editId, setEditId] = useState<string | 'new' | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [confirm, setConfirm] = useState<{ id: string; action: 'cancel' | 'delete'; title: string; postType: PostType } | null>(null);

  const coverInputRef = useRef<HTMLInputElement>(null);
  const bodyInputRef = useRef<HTMLInputElement>(null);

  const fetchResources = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/resources');
      const data = await res.json();
      if (data.success) setResources(data.resources || []);
      else setError(data.error || 'Failed to load resources');
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchResources(); }, [fetchResources]);

  const previewHtml = useMemo(
    () => DOMPurify.sanitize(marked.parse(form.body || '', { breaks: true }) as string),
    [form.body]
  );

  // Upload a file to S3 via presigned URL; returns the public file URL.
  const uploadImage = async (file: File): Promise<string> => {
    const res = await api.post('/admin/resources/upload-url', { fileName: file.name, fileType: file.type });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Could not get an upload URL');
    const put = await fetch(data.uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
    if (!put.ok) throw new Error('Upload to storage failed');
    return data.fileUrl as string;
  };

  const handleCoverFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true); setError('');
    try {
      const url = await uploadImage(file);
      setForm((f) => ({ ...f, cover_image_url: url }));
      setSuccess('Cover image uploaded.');
    } catch (err: any) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleBodyImageFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true); setError('');
    try {
      const url = await uploadImage(file);
      setForm((f) => ({ ...f, body: (f.body ? f.body + '\n\n' : '') + `![image](${url})\n` }));
      setSuccess('Image inserted into the body.');
    } catch (err: any) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const openNew = () => { setForm(EMPTY_FORM); setSlugTouched(false); setEditId('new'); };

  const openEdit = (r: Resource) => {
    setForm({
      category: r.category, post_type: r.postType, title: r.title, slug: r.slug || '',
      description: r.description || '', body: r.body || '', cover_image_url: r.coverImageUrl || '',
      external_url: r.externalUrl || '', display_date: r.displayDate || '', sort_order: r.sortOrder || 0,
      status: r.status,
    });
    setSlugTouched(true);
    setEditId(r.id);
  };

  const onTitleChange = (title: string) => {
    setForm((f) => ({ ...f, title, slug: f.post_type === 'hosted' && !slugTouched ? slugify(title) : f.slug }));
  };

  const saveForm = async () => {
    if (!form.title.trim()) { setError('Title is required'); return; }
    const hosted = form.post_type === 'hosted';
    if (hosted && !form.slug.trim()) { setError('Hosted posts need a slug'); return; }
    setSaving(true); setError(''); setSuccess('');
    try {
      const payload: any = {
        category: form.category, post_type: form.post_type, title: form.title.trim(),
        description: form.description, display_date: form.display_date,
        sort_order: Number(form.sort_order) || 0,
        external_url: hosted ? null : form.external_url,
        slug: hosted ? slugify(form.slug) : null,
        body: hosted ? form.body : null,
        body_format: 'markdown',
        cover_image_url: hosted ? (form.cover_image_url || null) : null,
      };
      let res: Response;
      if (editId === 'new') {
        payload.status = form.status;
        res = await api.post('/admin/resources', payload);
      } else {
        res = await apiRequest(`/admin/resources/${editId}`, { method: 'PATCH', body: JSON.stringify(payload) });
      }
      const data = await res.json();
      if (data.success) { setSuccess(editId === 'new' ? 'Resource created.' : 'Resource updated.'); setEditId(null); fetchResources(); }
      else setError(data.error || 'Save failed');
    } catch {
      setError('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id: string, status: Status) => {
    setProcessing(id); setError(''); setSuccess('');
    try {
      const res = await api.post(`/admin/resources/${id}/status`, { status });
      const data = await res.json();
      if (data.success) { setSuccess(`Status set to ${status}.`); fetchResources(); }
      else setError(data.error || 'Status change failed');
    } catch { setError('Status change failed'); }
    finally { setProcessing(null); }
  };

  const doDelete = async (id: string) => {
    setProcessing(id); setError(''); setSuccess('');
    try {
      const res = await api.delete(`/admin/resources/${id}`);
      const data = await res.json();
      if (data.success) { setSuccess('Resource deleted.'); fetchResources(); }
      else setError(data.error || 'Delete failed');
    } catch { setError('Delete failed'); }
    finally { setProcessing(null); }
  };

  const runConfirm = () => {
    if (!confirm) return;
    const c = confirm;
    setConfirm(null);
    if (c.action === 'delete') doDelete(c.id);
    else setStatus(c.id, 'cancelled');
  };

  const renderActions = (r: Resource) => {
    const busy = processing !== null || saving;
    const btns: React.ReactNode[] = [
      <Button key="edit" size="small" variant="outlined" disabled={busy} onClick={() => openEdit(r)} sx={tealOutlined}>Edit</Button>,
    ];
    if (r.status !== 'published') {
      btns.push(<Button key="pub" size="small" variant="contained" disabled={busy} onClick={() => setStatus(r.id, 'published')} sx={tealContained}>Publish</Button>);
    } else {
      btns.push(<Button key="hold" size="small" variant="outlined" disabled={busy} onClick={() => setStatus(r.id, 'draft')} sx={tealOutlined}>Hold</Button>);
    }
    if (r.status === 'cancelled') {
      btns.push(<Button key="restore" size="small" variant="outlined" disabled={busy} onClick={() => setStatus(r.id, 'draft')} sx={tealOutlined}>Restore</Button>);
    } else {
      btns.push(<Button key="cancel" size="small" variant="outlined" disabled={busy} onClick={() => setConfirm({ id: r.id, action: 'cancel', title: r.title, postType: r.postType })} sx={redOutlined}>Cancel</Button>);
    }
    btns.push(<Button key="del" size="small" variant="outlined" disabled={busy} onClick={() => setConfirm({ id: r.id, action: 'delete', title: r.title, postType: r.postType })} sx={redOutlined}>Delete</Button>);
    return <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>{btns}</Box>;
  };

  const isHosted = form.post_type === 'hosted';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>Resources</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew} sx={tealContained}>New Resource</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#2D6E6A' }} /></Box>
      ) : resources.length === 0 ? (
        <Alert severity="info">No resources yet. Use “New Resource” to add one.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#F5F5F5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Order</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {resources.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{r.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.displayDate}</Typography>
                  </TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{r.category}</TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{r.postType}</TableCell>
                  <TableCell>
                    <Chip size="small" label={STATUS_CHIP[r.status].label}
                      sx={{ bgcolor: STATUS_CHIP[r.status].bg, color: STATUS_CHIP[r.status].fg, fontWeight: 600 }} />
                  </TableCell>
                  <TableCell align="center">{r.sortOrder}</TableCell>
                  <TableCell align="right">{renderActions(r)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* hidden file inputs for uploads */}
      <input type="file" accept="image/*" hidden ref={coverInputRef} onChange={handleCoverFile} />
      <input type="file" accept="image/*" hidden ref={bodyInputRef} onChange={handleBodyImageFile} />

      {/* Create / edit form */}
      <Dialog open={editId !== null} onClose={() => setEditId(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{editId === 'new' ? 'New Resource' : 'Edit Resource'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField select label="Category" size="small" sx={{ minWidth: 160 }}
                value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as Category })}>
                <MenuItem value="blog">Blog</MenuItem>
                <MenuItem value="whitepaper">Whitepaper</MenuItem>
                <MenuItem value="press">Press</MenuItem>
              </TextField>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Type</Typography>
                <ToggleButtonGroup size="small" exclusive value={form.post_type}
                  onChange={(_, v) => v && setForm({ ...form, post_type: v })}>
                  <ToggleButton value="external" sx={{ textTransform: 'none' }}>External link</ToggleButton>
                  <ToggleButton value="hosted" sx={{ textTransform: 'none' }}>Hosted</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>

            <TextField label="Title" size="small" fullWidth value={form.title}
              onChange={(e) => onTitleChange(e.target.value)} />

            <TextField label="Description (card excerpt)" size="small" fullWidth multiline minRows={2}
              value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField label="Display date" size="small" placeholder="e.g. June 2026"
                value={form.display_date} onChange={(e) => setForm({ ...form, display_date: e.target.value })} />
              <TextField label="Sort order" size="small" type="number" sx={{ width: 120 }}
                value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} />
            </Box>

            {!isHosted ? (
              <TextField label="External URL" size="small" fullWidth placeholder="https://www.linkedin.com/posts/…"
                value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} />
            ) : (
              <>
                <TextField label="Slug" size="small" fullWidth helperText="Used in the post URL: /resources/<slug>"
                  value={form.slug}
                  onChange={(e) => { setSlugTouched(true); setForm({ ...form, slug: e.target.value }); }} />

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField label="Cover image URL" size="small" fullWidth
                    placeholder="Upload a file, or paste a URL"
                    value={form.cover_image_url} onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })} />
                  <Button variant="outlined" disabled={uploading} sx={{ ...tealOutlined, mt: 0.25, whiteSpace: 'nowrap' }}
                    startIcon={uploading ? <CircularProgress size={14} /> : <ImageOutlinedIcon />}
                    onClick={() => coverInputRef.current?.click()}>Upload</Button>
                </Box>
                {form.cover_image_url && (
                  <Box component="img" src={form.cover_image_url} alt="cover preview"
                    sx={{ maxHeight: 140, borderRadius: 2, objectFit: 'cover', alignSelf: 'flex-start' }} />
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Body (Markdown — tables &amp; images supported)</Typography>
                  <Button size="small" variant="outlined" disabled={uploading} sx={tealOutlined}
                    startIcon={uploading ? <CircularProgress size={14} /> : <ImageOutlinedIcon />}
                    onClick={() => bodyInputRef.current?.click()}>Insert image</Button>
                </Box>
                <TextField fullWidth multiline minRows={10}
                  value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
                  helperText="# heading · **bold** · - list · > quote · [text](url) · ![alt](image-url) · | table | col |"
                  InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.9rem' } }} />

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Preview (matches the published page)
                  </Typography>
                  {form.body.trim() ? (
                    <Box sx={previewSx} dangerouslySetInnerHTML={{ __html: previewHtml }} />
                  ) : (
                    <Box sx={{ ...previewSx, color: '#9AA8A5', fontStyle: 'italic' }}>Start typing to see a preview…</Box>
                  )}
                </Box>
              </>
            )}

            {editId === 'new' && (
              <TextField select label="Initial status" size="small" sx={{ minWidth: 160 }}
                value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as Status })}>
                <MenuItem value="draft">Draft (hidden)</MenuItem>
                <MenuItem value="published">Published (live)</MenuItem>
              </TextField>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditId(null)} sx={{ textTransform: 'none', color: 'text.secondary' }}>Cancel</Button>
          <Button variant="contained" disabled={saving || uploading} onClick={saveForm}
            startIcon={saving ? <CircularProgress size={14} /> : undefined} sx={tealContained}>
            {editId === 'new' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm destructive actions — copy is type-aware for delete */}
      <Dialog open={confirm !== null} onClose={() => setConfirm(null)}>
        {confirm && (() => {
          const isHostedDelete = confirm.action === 'delete' && confirm.postType === 'hosted';
          const title = confirm.action === 'cancel'
            ? 'Cancel resource?'
            : isHostedDelete ? 'Delete post permanently?' : 'Remove from Resources?';
          const body = confirm.action === 'cancel'
            ? `Set “${confirm.title}” to Cancelled. It will be hidden from the public page but kept, and you can Restore it later.`
            : isHostedDelete
              ? `This permanently deletes “${confirm.title}” and its content from Percoro. This cannot be undone.`
              : `This removes “${confirm.title}” from your Resources page only. The original post still lives at its source (e.g. LinkedIn) and isn’t affected — to delete the actual post, do it there.`;
          const cta = confirm.action === 'cancel'
            ? 'Cancel resource'
            : isHostedDelete ? 'Delete permanently' : 'Remove from page';
          return (
            <>
              <DialogTitle sx={{ fontWeight: 600 }}>{title}</DialogTitle>
              <DialogContent><DialogContentText>{body}</DialogContentText></DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => setConfirm(null)} sx={{ textTransform: 'none', color: 'text.secondary' }}>Back</Button>
                <Button variant="contained" onClick={runConfirm}
                  sx={{ textTransform: 'none', borderRadius: 2, bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' } }}>
                  {cta}
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </Box>
  );
};

export default ResourcesTab;
